/**
 * Payroll admin API layer.
 *
 * Deliberately a mirror of `attendance/attendanceService.js` — same envelope,
 * same unwrapping, same axios instance — so the two modules are one convention
 * rather than two.
 *
 * Nothing here computes money. Every figure on a slip is produced by
 * App\Services\Payroll\PayRunService and read back; the browser's only jobs are
 * choosing a period and pressing Compute. The screen this replaces added eleven
 * fields with `parseInt()` and posted the total for the server to store.
 */

const PREFIX = 'api/payroll'

function get(url, params) {
	return window.axios.get(url, { params }).then((r) => r.data.data)
}

function post(url, body) {
	return window.axios.post(url, body).then((r) => r.data.data)
}

function put(url, body) {
	return window.axios.put(url, body).then((r) => r.data.data)
}

/** Pull a readable message out of a Laravel error response. */
export function errorMessage(error, fallback = 'Something went wrong') {
	const data = error && error.response && error.response.data
	if (data) {
		if (data.errors) {
			const first = Object.values(data.errors)[0]
			if (Array.isArray(first) && first[0]) return first[0]
		}
		if (typeof data.message === 'string' && data.message) return data.message
	}
	return fallback
}

// ---------------------------------------------------------------------------
// Running payroll
// ---------------------------------------------------------------------------

export const runService = {
	list: (params) => get(`${PREFIX}/runs`, params),

	/** Opens the draft for a month, or returns the one already there. */
	open: (month, storeid) => post(`${PREFIX}/runs`, { month, storeid }),

	show: (id) => get(`${PREFIX}/runs/${id}`),

	/**
	 * Safe to press repeatedly — a recompute rebuilds and reverses cleanly.
	 * With employeeIds only those slips are rebuilt; everyone else's is left alone.
	 */
	compute: (id, employeeIds, discardEditsFor) =>
		post(`${PREFIX}/runs/${id}/compute`, {
			...(employeeIds ? { employee_ids: employeeIds } : {}),
			// Hand-edited slips are left alone unless named here.
			...(discardEditsFor && discardEditsFor.length ? { discard_edits_for: discardEditsFor } : {}),
		}),

	/**
	 * Refused by the server unless the attendance period is locked. With
	 * employeeIds only those drafts are approved and the run stays open.
	 */
	approve: (id, employeeIds) =>
		post(`${PREFIX}/runs/${id}/approve`, employeeIds ? { employee_ids: employeeIds } : {}),

	/**
	 * Undo an approval. The server reverses each slip's payments and ledger
	 * credit and puts it back to draft; it must be recomputed before approving.
	 * With employeeIds only those slips are reopened.
	 */
	reopen: (id, reason, employeeIds, confirmLaterApproved) =>
		post(`${PREFIX}/runs/${id}/reopen`, {
			reason,
			...(employeeIds ? { employee_ids: employeeIds } : {}),
			...(confirmLaterApproved ? { confirm_later_approved: true } : {}),
		}),

	remove: (id) => window.axios.delete(`${PREFIX}/runs/${id}`),
}

export const slipService = {
	show: (id) => get(`${PREFIX}/slips/${id}`),

	/**
	 * One save: day figures, period dates, line amounts, extra lines, removals.
	 * An approved slip is reopened, edited and approved again on the server, so
	 * its ledger entry is reversed and reposted exactly once however much
	 * changed - which is why this is one call and not one call per field.
	 */
	update: (id, body) => put(`${PREFIX}/slips/${id}`, body),

	/** Approve this slip alone. Does not require the attendance period to be locked. */
	approve: (id, reason) => post(`${PREFIX}/slips/${id}/approve`, reason ? { reason } : {}),

	/** Undo this slip's approval: payments and ledger credit reversed, back to draft. */
	reopen: (id, reason, confirmLaterApproved) =>
		post(`${PREFIX}/slips/${id}/reopen`, {
			reason,
			...(confirmLaterApproved ? { confirm_later_approved: true } : {}),
		}),

	/** What a paid slip needs before it can be edited. */
	reversePayment: (paymentId, reason) => post(`${PREFIX}/payments/${paymentId}/reverse`, { reason }),

	addLine: (id, body) => post(`${PREFIX}/slips/${id}/lines`, body),

	removeLine: (id, lineId) => window.axios.delete(`${PREFIX}/slips/${id}/lines/${lineId}`),

	pay: (id, body) => post(`${PREFIX}/slips/${id}/pay`, body),

	cancel: (id, reason) => post(`${PREFIX}/slips/${id}/cancel`, { reason }),
}

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------

export const componentService = {
	list: () => get(`${PREFIX}/components`),
	create: (body) => post(`${PREFIX}/components`, body),
	update: (id, body) => put(`${PREFIX}/components/${id}`, body),
	remove: (id) => window.axios.delete(`${PREFIX}/components/${id}`),
}

/**
 * Pay is effective-dated, so there is no update — saving supersedes. The server
 * closes the row in force and inserts a new one, which is why a slip from last
 * year still resolves to the basis it was actually computed on.
 */
export const structureService = {
	list: () => get(`${PREFIX}/structures`),
	history: (employeeId) => get(`${PREFIX}/employees/${employeeId}/structures`),
	save: (employeeId, body) => post(`${PREFIX}/employees/${employeeId}/structures`, body),
}

export const incentiveRuleService = {
	list: () => get(`${PREFIX}/incentive-rules`),
	create: (body) => post(`${PREFIX}/incentive-rules`, body),
	update: (id, body) => put(`${PREFIX}/incentive-rules/${id}`, body),
	remove: (id) => window.axios.delete(`${PREFIX}/incentive-rules/${id}`),
}

// ---------------------------------------------------------------------------
// Advances and the employee account
// ---------------------------------------------------------------------------

export const advanceService = {
	list: (params) => get(`${PREFIX}/advances`, params),
	create: (body) => post(`${PREFIX}/advances`, body),
	update: (id, body) => put(`${PREFIX}/advances/${id}`, body),
	recoveries: (id) => get(`${PREFIX}/advances/${id}/recoveries`),
}

export const payLedgerService = {
	list: () => get(`${PREFIX}/ledger`),
	statement: (employeeId, params) => get(`${PREFIX}/ledger/${employeeId}`, params),
}
